import { AppList } from '@ui/applications';
import { CheckCircleOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { ColumnType } from 'antd/es/table';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { FieldUsageSelector } from '@ui/tags/FieldUsageSelector';
import { getClosestSelectionTable } from '@lib/popups';
import React from 'react';
import { SalesforceLogo } from '@ui/icons';

import {
	CodeWithHighlights,
	DataDictionaryEntry,
	DataDictionarySchema,
	DataSensitivityLevelId,
	KnownApexClassStatus,
	SchemaFormat
} from '@sonar-software/sonar-core';
import { ComplianceCategorySelector, DataSensitivitySelector } from '@ui/tags';
import {
	DateTimeColumn,
	DefinitionColumn,
	FollowCheckbox,
	Formula,
	NotesColumn,
	PrimaryColumn,
	TagsColumn
} from './columns';
import { Flex, Tooltip } from 'antd';
import { getLocalDateTime, lowerFirst } from '@sonar-software/sonar-ts-lib';
import { SalesforceDescription, SalesforceHelpText, SonarText } from '@ui/text';
import { SonarObjectOwner, SonarObjectStakeholders } from '@ui/general';

type ColumnDefinition = ColumnType<DataDictionaryEntry>;

const isSalesforceSyncColumn: Record<string, boolean> = {
	ComplianceCategory: true,
	DataSensitivityLevelId: true,
	Description: true,
	FieldUsageId: true,
	HelpText: true
};

const baseClassName = 'data-dictionary-column-header';

export function translateApiResultsToDataDictionaryColumns(
	schemaArray: Array<DataDictionarySchema>,
	getPopupContainer?: (trigger: HTMLElement) => HTMLElement
): Array<ColumnDefinition> {
	const columns = schemaArray
		.filter((schema) => schema.format !== SchemaFormat.None)
		.map((schema: DataDictionarySchema) => {
			const { columnId, format } = schema;

			const baseDefinition: ColumnDefinition = {
				className: classNames(baseClassName, format),
				dataIndex: lowerFirst(columnId),
				title: isSalesforceSyncColumn[ columnId ] ?
					<Flex
						align='center'
						justify='space-between'>
						{ schema.columnName }
						<Tooltip title={ COPY_SPECIFIC.CHANGES_SYNC_TO_SALESFORCE }>
							<div className='sfdc-sync-indicator'><SalesforceLogo /></div>
						</Tooltip>
					</Flex> :
					schema.columnName
			};

			switch (format) {
				case SchemaFormat.Primary:
					return {
						...baseDefinition,
						fixed: true,
						render: (label: string, entry: DataDictionaryEntry) =>
							<PrimaryColumn
								entry={ entry }
								label={ label } />

					};
				case SchemaFormat.Notes:
					return {
						...baseDefinition,
						className: classNames(baseDefinition.className, 'no-padding-cell'),
						render: (notes: string, entry: DataDictionaryEntry) =>
							<NotesColumn
								getPopupContainer={ getPopupContainer }
								sonarObjectId={ entry.sonarObjectId } />

					};
				case SchemaFormat.Definition:
					return {
						...baseDefinition,
						className: classNames(baseDefinition.className, 'no-padding-cell'),
						render: (definition: string, entry: DataDictionaryEntry) =>
							<DefinitionColumn
								getPopupContainer={ getPopupContainer }
								sonarObjectId={ entry.sonarObjectId } />

					};
				case SchemaFormat.Checkmark:
					if (columnId === 'IsFollowing') {
						return {
							...baseDefinition,
							render: (checked: boolean, entry: DataDictionaryEntry) =>
								<FollowCheckbox sonarObjectId={ entry.sonarObjectId } />
						};
					}

					return {
						...baseDefinition,
						render: (checked: boolean) => (checked ? <CheckCircleOutlined /> : null)
					};
				case SchemaFormat.Boolean:
					return {
						...baseDefinition,
						render: (active: boolean) =>
							<SonarText>{ active ? COPY_SPECIFIC.ACTIVE : COPY_SPECIFIC.INACTIVE }</SonarText>

					};
				case SchemaFormat.DateTime:
					return {
						...baseDefinition,
						render: (dateTime: string) => <DateTimeColumn dateTime={ dateTime } />
					};
				case SchemaFormat.Owner:
					return {
						...baseDefinition,
						render: (owner: Array<number>, entry: DataDictionaryEntry) =>
							<SonarObjectOwner
								getPopupContainer={ getPopupContainer ?? getClosestSelectionTable }
								isSpreadsheet
								sonarObjectId={ entry.sonarObjectId } />

					};
				case SchemaFormat.Stakeholder:
					return {
						...baseDefinition,
						render: (stakeholder: Array<number>, entry: DataDictionaryEntry) =>
							<SonarObjectStakeholders
								getPopupContainer={ getPopupContainer ?? getClosestSelectionTable }
								isSpreadsheet
								sonarObjectId={ entry.sonarObjectId } />

					};
				case SchemaFormat.Date:
					return {
						...baseDefinition,
						render: (date: string) => <SonarText>{ getLocalDateTime(date).format('L') }</SonarText>
					};
				case SchemaFormat.Time:
					return {
						...baseDefinition,
						render: (date: string) => <SonarText>{ getLocalDateTime(date).format('LT') }</SonarText>
					};
				case SchemaFormat.Tags:
					return {
						...baseDefinition,
						render: (tagIds: Array<number>, entry: DataDictionaryEntry) =>
							<TagsColumn sonarObjectId={ entry.sonarObjectId } />

					};
				case SchemaFormat.Code:
					return {
						...baseDefinition,
						render: (formula: CodeWithHighlights | null) => <Formula formula={ formula } />
					};
				case SchemaFormat.Description:
					return {
						...baseDefinition,
						className: classNames(baseDefinition.className, 'no-padding-cell'),
						render: (description: string, entry: DataDictionaryEntry) =>
							<SalesforceDescription
								isSpreadsheet
								sonarObjectId={ entry.sonarObjectId } />

					};
				case SchemaFormat.HelpText:
					return {
						...baseDefinition,
						className: classNames(baseDefinition.className, 'no-padding-cell'),
						render: (helpText: string, entry: DataDictionaryEntry) =>
							<SalesforceHelpText
								isSpreadsheet
								sonarObjectId={ entry.sonarObjectId } />

					};
				case SchemaFormat.Apps:
					return {
						...baseDefinition,
						render: (appIds: Array<number>) => <AppList
							appIds={ appIds }
							direction='vertical'
							showNotMonitored />
					};
				default:
					if (format.includes(SchemaFormat.Enum)) {
						const enumType = format.substring(5);
						const enumBaseDefinition: ColumnDefinition = {
							...baseDefinition,
							className: classNames(baseClassName, 'enum')
						};

						switch (enumType) {
							case 'DataSensitivityLevelId':
								return {
									...enumBaseDefinition,
									className: classNames(baseDefinition.className, 'no-padding-cell'),
									render: (enumerable: DataSensitivityLevelId, entry: DataDictionaryEntry) =>
										<DataSensitivitySelector
											getPopupContainer={ getPopupContainer ?? getClosestSelectionTable }
											isSpreadsheet
											sonarObjectId={ entry.sonarObjectId } />
								};
							case 'ComplianceCategory':
								return {
									...enumBaseDefinition,
									className: classNames(baseDefinition.className, 'no-padding-cell'),
									render: (enumerable: number, entry: DataDictionaryEntry) =>
										<ComplianceCategorySelector
											getPopupContainer={ getPopupContainer ?? getClosestSelectionTable }
											isSpreadsheet
											sonarObjectId={ entry.sonarObjectId } />
								};
							case 'FieldUsageId':
								return {
									...enumBaseDefinition,
									className: classNames(baseDefinition.className, 'no-padding-cell'),
									render: (enumerable: number, entry: DataDictionaryEntry) =>
										<FieldUsageSelector
											getPopupContainer={ getPopupContainer ?? getClosestSelectionTable }
											isSpreadsheet
											sonarObjectId={ entry.sonarObjectId } />
								};
							case 'Status':
								return {
									...enumBaseDefinition,
									render: (enumerable: number) => KnownApexClassStatus[ enumerable ]
								};

							default:
								return enumBaseDefinition;
						}
					}

					return {
						...baseDefinition,
						className: classNames(baseClassName, 'text')
					};
			}
		});

	if (columns.length < 2) {
		columns.push({});
	}

	return columns;
}
